// ErrorBoundary.jsx
import React, { useState } from 'react';
import posthog from "posthog-js";
import { ErrorReporter } from '@/app/tracking';

const ErrorFallback = ({ error, errorInfo }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Safely access error properties with fallbacks  
  let message = error?.message || 'An unknown error occurred';
  let stack = errorInfo?.componentStack;

  // Check if this is a dynamic import failure (new version available)
  const isImportFailure = message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk');

  const handleReload = () => {
    window.location.reload();
  };

  if (isImportFailure) {
    // Don't report dynamic import failures as errors - they indicate a new version
    return (
      <div className="p-4 m-4 border border-blue-500 rounded bg-blue-50">
        <h2 className="text-xl font-bold text-blue-700 mb-3">
          New Version Available
        </h2>
        <p className="text-blue-600 mb-4">
          A new version of the application is available. Please reload the page to get the latest updates.
        </p>
        <button
          onClick={handleReload}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Reload Page
        </button>
      </div>
    );
  }

  // For other errors, report them and show the standard error UI
  let msg = "GlobalErrorBoundary " + window.location.pathname + ": " + message;
  ErrorReporter.error({ msg, stack });

  return (
    <div className="p-4 m-4 border border-red-500 rounded">
      <h2 className="text-xl font-bold text-red-600">
        Something went wrong
      </h2>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="mt-2 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded"
      >
        {isExpanded ? 'Hide' : 'Show'} details
      </button>
      {isExpanded && (
        <pre className="mt-2 p-2 bg-gray-100 rounded overflow-auto">
          {error?.message}
          <p className="text-xs text-muted-foreground">{window.location.pathname}</p>
          {errorInfo?.componentStack}
        </pre>
      )}
    </div>
  )
}

// Unfortunately we need to use a class component here as React still doesn't
// support error boundaries with hooks
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    posthog.captureException(error);
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <ErrorFallback
          error={this.state.error}
          errorInfo={null}
        />
      )
    }

    return this.props.children
  }
}